/**
 * @clearpath/shared-types/errors — the wire format that carries a
 * `GraphQLError`'s `extensions` across the AppSync boundary.
 *
 * WHY THIS EXISTS (ENG-655)
 * ------------------------------------------------------------------------
 * `services/appsync/RESOLVER-CONVENTIONS.md` told every handler author:
 *
 *   > **Lambda resolvers**: throw `new GraphQLError(message, { extensions:
 *   > { code } })`. AppSync surfaces `code` to clients.
 *
 * That last sentence is false, and had been since Phase 1. Two independent
 * losses sit between a thrown `GraphQLError` and the browser — both verified
 * against DEPLOYED staging on 2026-07-31:
 *
 *  1. The Node.js Lambda runtime serializes a thrown error to exactly
 *     `{ errorType, errorMessage, trace }`. Own properties — including
 *     `extensions` — are dropped. Direct invoke of
 *     `molarisai-platform-lambda-api-staging` returns:
 *
 *       { "errorType": "GraphQLError",
 *         "errorMessage": "Unauthorized: missing Cognito identity",
 *         "trace": [...] }          <- no `extensions` anywhere
 *
 *  2. AppSync's Lambda data source does not forward that payload's
 *     `errorType` either. It reports the invocation as `Lambda:Unhandled`,
 *     which is what the shared response mapping template
 *     (`$util.error($ctx.error.message, $ctx.error.type)`) then raises:
 *
 *       { "errorType": "Lambda:Unhandled", "errorInfo": null,
 *         "message": "org_unit not found" }   <- no `extensions` key at all
 *
 * Net effect: EVERY `err.extensions?.code === '...'` branch in every SPA was
 * dead code. A deliberate, typed refusal ("this case is on hold", "which unit
 * did you scan?", "step-up MFA required") degraded to a generic failure.
 *
 * `message` is the ONLY field that survives a throwing Lambda unmodified, so
 * that is the channel this envelope rides in. The api Lambda's single
 * catch-all in `services/api/src/handler.ts` appends the envelope; each SPA's
 * `errorEnvelopeLink` — three lines of `new ApolloLink(...)` around
 * `normalizeGraphQLResultErrors` below, see that function for why the link
 * itself cannot be shared — decodes it back into `extensions` and restores the
 * clean human `message` before any `onError` callback or component ever sees
 * the error. No component string-matches on error prose.
 *
 * The decoder ALSO reads AppSync's native `errorType` / `errorInfo` channel
 * (which JS pipeline resolvers populate via `util.error(msg, 'Conflict')`),
 * so a later infrastructure change that upgrades the response mapping
 * template to the 4-arg `$util.error(message, code, null, extensions)` form
 * is a pure improvement — it needs no client change and no flag day.
 *
 * This module is deliberately dependency-free (no `graphql`, no `@apollo/
 * client`) so both the api Lambda bundle and every browser bundle can import
 * it, and so the format has exactly ONE definition.
 */

/**
 * Sentinel separating the human message from the JSON envelope.
 *
 * Requirements it satisfies:
 *  - Vanishingly unlikely to occur in human error prose (so `decode` never
 *    fires on a genuine message).
 *  - Pure printable ASCII — it round-trips through the Lambda runtime's
 *    `errorMessage`, AppSync's VTL `$util.error(...)` and JSON without any
 *    escaping surprises.
 *  - Versioned, so a future format change is detectable rather than silently
 *    mis-parsed by an older client.
 *  - Appended as a SUFFIX, so a message that reaches an un-normalized
 *    consumer (an old cached SPA bundle, a curl by hand, an e2e assertion
 *    using `toMatch`) still leads with the readable sentence.
 */
export const ERROR_ENVELOPE_MARKER = " #cp-err/1#";

/** A decoded envelope: the human-facing message plus the typed extensions. */
export interface DecodedErrorEnvelope {
  /**
   * The original message with the envelope stripped — the human half of
   * what the handler wrote, restored to what it would have read before the
   * envelope was appended. NOT a claim that it is safe to show a user: it is
   * server prose written for whoever reads the handler's throw site, not
   * copy reviewed for an end-user screen. Whether and how a caller may
   * render a failure is the error-copy decision
   * (docs/architecture-decisions/ENG-2539-operator-error-copy.md), not this
   * module's concern — this module's only job is decoding the wire format
   * back into `{ message, extensions }` faithfully.
   */
  readonly message: string;
  /** The `GraphQLError.extensions` the server intended the client to read. */
  readonly extensions: Record<string, unknown>;
}

/**
 * Hard cap on the encoded extensions payload. Guards against a handler
 * stuffing an unbounded list (e.g. one `violations` entry per imported row)
 * into `extensions` and producing a multi-megabyte error message. On
 * overflow the envelope degrades to `{ code }` only — the code is the part
 * every branch keys on, so a refusal stays typed even when its detail is
 * dropped.
 */
export const MAX_ENCODED_EXTENSIONS_CHARS = 8192;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The human half of a message: everything before the FIRST marker.
 *
 * The marker is only ever appended by the server, as a suffix, so any marker
 * already inside a message being encoded is one of exactly two things:
 *
 *  - a re-wrap (a nested handler caught and re-threw an already-encoded
 *    error), where truncating restores the original human sentence and the
 *    outer encode then re-appends the same envelope — i.e. idempotent; or
 *  - user-controlled text that a handler echoed into its message. Many do:
 *    `unsupported targetField "${m.targetField}"`, `unknown role audience:
 *    ${input}`. Without this truncation such a message would (a) make the
 *    whole encode a no-op, silently DROPPING the server's real code, and
 *    (b) leak a raw `#cp-err/1#{...}` into text a user reads. Truncating
 *    means the trailing envelope is always the server's own.
 *
 * Impact of the forgery it closes is limited — a GraphQL error is only ever
 * delivered to the caller who provoked it, so an injected envelope could only
 * mislead the injector's own browser — but "the server's real refusal code is
 * discardable by user input" is not a property worth keeping.
 */
function humanPartOf(message: string): string {
  const at = message.indexOf(ERROR_ENVELOPE_MARKER);
  return at < 0 ? message : message.slice(0, at).trimEnd();
}

/**
 * Append the machine-readable envelope to a human error message.
 *
 * Returns the message with nothing appended when there is nothing typed to
 * carry (no extensions, or extensions with no own keys) — an untyped error
 * must stay untyped end to end so the client can still tell "deliberate
 * refusal" from "something unexpected broke".
 *
 * Idempotent, and immune to a message that already contains a marker: see
 * `humanPartOf`.
 */
export function encodeErrorEnvelope(
  message: string,
  extensions?: Readonly<Record<string, unknown>> | null,
): string {
  const human = humanPartOf(message);
  if (!isPlainRecord(extensions)) return human;
  const keys = Object.keys(extensions);
  if (keys.length === 0) return human;

  let payload: string;
  try {
    payload = JSON.stringify(extensions);
  } catch {
    // Circular / non-serializable extensions: fall back to the code alone
    // rather than throwing from inside an error path.
    payload = "";
  }
  // Measure the cap on what is actually EMITTED, not on the pre-escape JSON.
  // `escapeMarkerInPayload` grows the string by one character per solidus, so
  // a slash-dense payload (URLs, mime types, file paths) that fits under the
  // cap before escaping can breach it after — checking the raw length would
  // let an envelope reach nearly twice `MAX_ENCODED_EXTENSIONS_CHARS`, which
  // is precisely the unbounded-error-message case the cap exists to prevent.
  let encoded = payload ? escapeMarkerInPayload(payload) : "";
  if (!encoded || encoded.length > MAX_ENCODED_EXTENSIONS_CHARS) {
    const code = extensions["code"];
    if (typeof code !== "string") return human;
    encoded = escapeMarkerInPayload(JSON.stringify({ code }));
    if (encoded.length > MAX_ENCODED_EXTENSIONS_CHARS) return human;
  }
  return `${human}${ERROR_ENVELOPE_MARKER}${encoded}`;
}

/**
 * Make the marker unrepresentable inside an encoded payload.
 *
 * `decodeErrorEnvelope` locates the payload with `lastIndexOf(MARKER)`, which
 * is only correct while the payload itself cannot contain the marker. It can:
 * every string VALUE in `extensions` is data the server read back out of the
 * database, and `JSON.stringify` does not escape `/`. A tenant that names a
 * customer `Northline #cp-err/1# Partners` puts the literal marker inside the
 * payload; `lastIndexOf` then points at THAT one, `tail` is a fragment of the
 * JSON rather than the whole of it, `JSON.parse` throws, and the decode falls
 * back to empty `extensions`. It fails closed — no forged code is ever
 * accepted — but the effect is that a typed refusal silently degrades to an
 * untyped one, disabling every extensions-driven message for that row's
 * tenant until the name is edited. One bad name, one whole feature off.
 *
 * `\/` is a valid JSON escape for `/` (RFC 8259 §7) that `JSON.parse` maps
 * straight back to `/`, so escaping every solidus in the encoded payload
 * removes the only character sequence that could reproduce the marker, and
 * costs nothing on the decode side. Backward compatible in both directions: an
 * older client still finds the (now unambiguous) trailing marker and its
 * `JSON.parse` restores the `/`, and this encoder's output is unchanged for
 * every payload that contains no solidus at all.
 */
function escapeMarkerInPayload(payload: string): string {
  return payload.split("/").join("\\/");
}

/**
 * Split a message back into `{ message, extensions }`.
 *
 * Returns `null` when the message carries no envelope.
 *
 * Fails CLOSED on a corrupt envelope: the marker (and everything after it) is
 * always stripped so a raw sentinel never reaches a user, but `extensions`
 * comes back empty, which makes the error read as unexpected rather than as a
 * typed refusal whose code we guessed at.
 */
export function decodeErrorEnvelope(message: unknown): DecodedErrorEnvelope | null {
  if (typeof message !== "string") return null;
  // Read the payload from the LAST marker (the server appends its envelope as
  // a suffix, so the trailing one is authoritative) but cut the human half at
  // the FIRST one, so a message that somehow carries two — an un-encoded error
  // from a non-api Lambda that echoed user input, say — can never render a raw
  // sentinel to a user.
  const at = message.lastIndexOf(ERROR_ENVELOPE_MARKER);
  if (at < 0) return null;

  const head = humanPartOf(message);
  const tail = message.slice(at + ERROR_ENVELOPE_MARKER.length);
  let parsed: unknown;
  try {
    parsed = JSON.parse(tail);
  } catch {
    return { message: head, extensions: {} };
  }
  return { message: head, extensions: isPlainRecord(parsed) ? parsed : {} };
}

/** Minimal structural shape of one entry in a GraphQL response's `errors`. */
export interface WireGraphQLError {
  message?: unknown;
  extensions?: unknown;
  /** AppSync-native code channel (`Lambda:Unhandled` when a Lambda threw). */
  errorType?: unknown;
  /** AppSync-native structured-detail channel (`$util.error` 4-arg form). */
  errorInfo?: unknown;
  [key: string]: unknown;
}

/**
 * AppSync reports a thrown Lambda as `Lambda:Unhandled` (and other
 * data-source failures as `Lambda:<something>`). Those are transport-level
 * markers, never a domain code — treating them as one would make every
 * unexpected crash look like a typed refusal, which is exactly the
 * distinction this whole mechanism exists to preserve.
 */
function isTransportErrorType(errorType: string): boolean {
  return errorType.length === 0 || errorType.startsWith("Lambda:");
}

/**
 * Normalize ONE wire error into a standard GraphQL error whose
 * `extensions.code` is populated, and whose `message` is the clean human
 * sentence.
 *
 * Precedence, highest first:
 *  1. The `#cp-err/1#` envelope on `message` (what the api Lambda emits).
 *  2. `extensions` already present on the wire (the local-backend dev shim
 *     and any non-AppSync transport put the real thing here).
 *  3. AppSync's native `errorType` + `errorInfo` (JS pipeline resolvers'
 *     `util.error(msg, 'Conflict')`, and the future 4-arg VTL upgrade).
 *
 * Returns the SAME object reference when nothing needed changing, so callers
 * can cheaply detect a no-op. Never mutates its input.
 */
export function normalizeGraphQLError<T extends WireGraphQLError>(error: T): T {
  if (!isPlainRecord(error)) return error;

  const wireExtensions = isPlainRecord(error.extensions) ? error.extensions : undefined;
  const decoded = decodeErrorEnvelope(error.message);

  const extensions: Record<string, unknown> = { ...wireExtensions, ...decoded?.extensions };
  let adoptedErrorType = false;

  if (typeof extensions["code"] !== "string") {
    const errorType = error.errorType;
    if (typeof errorType === "string" && !isTransportErrorType(errorType)) {
      extensions["code"] = errorType;
      adoptedErrorType = true;
      if (isPlainRecord(error.errorInfo)) {
        // errorInfo is lower precedence than anything already resolved.
        for (const [key, value] of Object.entries(error.errorInfo)) {
          if (!(key in extensions)) extensions[key] = value;
        }
      }
    }
  }

  // Nothing was decoded and no native errorType was adopted -> the wire error
  // already was what it will be. Hand back the same reference.
  if (decoded === null && !adoptedErrorType) return error;

  const changedMessage = decoded !== null && decoded.message !== error.message;
  return {
    ...error,
    ...(changedMessage ? { message: decoded.message } : {}),
    ...(Object.keys(extensions).length > 0 ? { extensions } : {}),
  };
}

/**
 * Normalize a GraphQL response's `errors` array. Returns the SAME array
 * reference when every entry was already normal, so the caller can skip
 * rebuilding the response object.
 */
export function normalizeGraphQLErrors<T extends WireGraphQLError>(
  errors: readonly T[],
): readonly T[] {
  let changed = false;
  const next = errors.map((error) => {
    const normalized = normalizeGraphQLError(error);
    if (normalized !== error) changed = true;
    return normalized;
  });
  return changed ? next : errors;
}

/**
 * Normalize a whole GraphQL transport result (`{ data, errors }`) in one call.
 *
 * This is the entry point every SPA's Apollo link chain uses:
 *
 * ```ts
 * const errorEnvelopeLink = new ApolloLink((operation, forward) =>
 *   forward(operation).map((result) => normalizeGraphQLResultErrors(result)),
 * );
 * ```
 *
 * It is deliberately generic and Apollo-agnostic. The workspace resolves TWO
 * `@apollo/client` instances (five SPAs pin `graphql-ws@^5`, `apps/main` gets
 * `@6`, and pnpm keys the peer resolution on that), so an `ApolloLink` built in
 * a shared package cannot be assigned into every app's chain. Sharing this
 * pure function instead — and letting each app spend one line on its own
 * `ApolloLink` — keeps the decoding logic in exactly one tested place without
 * touching that dependency split.
 *
 * Returns the SAME result reference when nothing needed normalizing.
 */
export function normalizeGraphQLResultErrors<T>(result: T): T {
  const errors = (result as { errors?: unknown } | null | undefined)?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return result;
  const normalized = normalizeGraphQLErrors(errors as readonly WireGraphQLError[]);
  if (normalized === errors) return result;
  return { ...(result as object), errors: normalized } as T;
}

// ---------------------------------------------------------------------------
// ENG-2479 — one shared duck-typing walk for ApolloError /
// GraphQLError-shaped values.
//
// `packages/permissions-ui/src/deny.ts` (ENG-62) invented this walk first,
// privately, and every caller that needs to pull `extensions` off an unknown
// thrown/rejected value has since grown its own private copy rather than
// reaching into another package's `src/`. This module already has NO
// framework dependency (no `graphql`, no `@apollo/client`) and is already
// reachable through the `./errors` subpath, so it is the natural shared
// home — a future `denyMessageFromError` caller (or any other consumer) can
// import `graphQLErrorCandidates` / `extensionsOf` from here without adding a
// dependency on `@clearpath/permissions-ui`.
//
// This is an ADDITIVE pin only. `denyMessageFromError` is NOT migrated to
// call these — see the divergence note on `graphQLErrorCandidates` below —
// and every existing export/signature in this file is unchanged. The 35 other private walks across the monorepo are
// a follow-up migration, not this change.
// ---------------------------------------------------------------------------

/**
 * Reduce an unknown thrown/rejected value to the list of GraphQL-error-shaped
 * candidates worth inspecting for `extensions`.
 *
 *  - Not a plain object (`null`, `undefined`, a string, an array, ...): `[]`
 *    — nothing to walk.
 *  - An ApolloError-like object whose `graphQLErrors` is a NON-EMPTY array:
 *    that array, unchanged.
 *  - Anything else (a bare GraphQLError-like object with `extensions`
 *    directly, a plain object with no `graphQLErrors`, or an ApolloError-like
 *    object whose `graphQLErrors` is an EMPTY array): `[err]` — the value
 *    itself is the sole candidate.
 *
 * DIVERGES FROM `deny.ts` ON PURPOSE, for the empty-array case: `deny.ts`'s
 * private walk (`packages/permissions-ui/src/deny.ts:173`) is
 * `Array.isArray(graphQLErrors) ? graphQLErrors : [err]`, with no non-empty
 * test, so an ApolloError whose `graphQLErrors` is `[]` walks ZERO
 * candidates there. This function instead falls back to `[err]` for that
 * same input. The two happen to reach the same outward RESULT for every
 * shape `denyMessageFromError` is proven against (`deny.test.ts`) — `err`
 * itself carries no `.extensions` either in that case, so `extensionsOf(err)`
 * is `null` and the caller's loop still finds nothing — but they are not the
 * same walk. `deny.ts` is deliberately NOT migrated onto this primitive here:
 * doing so is a behavior-preserving refactor that deserves its own test proof,
 * not a change folded into the one introducing a brand new primitive. A future consumer that needs `deny.ts`'s exact (empty-array
 * -blind) semantics must not assume this function reproduces them — read
 * this note first.
 */
export function graphQLErrorCandidates(err: unknown): readonly unknown[] {
  if (!isPlainRecord(err)) return [];
  const graphQLErrors = err["errors"] ?? err["graphQLErrors"];
  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) return graphQLErrors;
  return [err];
}

/** GraphQL errors carried by Apollo 4 or the historical Apollo 3 envelope. */
export function graphQLErrorsOf(err: unknown): readonly GraphQLErrorRecord[] {
  if (!isPlainRecord(err)) return [];
  const errors = err["errors"] ?? err["graphQLErrors"];
  if (!Array.isArray(errors)) return [];
  return errors.filter(isPlainRecord).map((error) => ({
    ...error,
    message: typeof error["message"] === "string" ? error["message"] : "",
    extensions: isPlainRecord(error["extensions"]) ? error["extensions"] : undefined,
  }));
}

export interface GraphQLErrorRecord {
  readonly message: string;
  readonly extensions?: Record<string, unknown> | undefined;
}

/**
 * Pull a candidate's `extensions` as a plain record, or `null` when there is
 * nothing usable there.
 *
 * Returns `null` for: a non-object candidate, a missing `extensions` key, or
 * an `extensions` value that is `null`, an array, or a primitive (string,
 * number, boolean). Only a plain object counts.
 */
export function extensionsOf(candidate: unknown): Record<string, unknown> | null {
  if (!isPlainRecord(candidate)) return null;
  const direct = candidate["extensions"];
  return isPlainRecord(direct) ? direct : null;
}

/**
 * ENG-3054 AD-6 — the one sentence for a customer-access change refused
 * because another access change in the same organization holds the per-org
 * lock, or won a race with it (`extensions.code === 'Conflict'`,
 * `retryable: true`). Shared so the server message
 * (services/api/src/catalog/entitlements/budget.ts) and the admin toast copy
 * (apps/main/src/pages/admin/productsTabErrors.ts) cannot drift apart.
 */
export const ACCESS_CHANGE_IN_PROGRESS_MESSAGE = "Another access change is in progress. Try again.";

/**
 * ENG-3054 AD-6 — `extensions.reason` of a customer-access change refused by
 * the organization's rolling 24h limit on customers losing the ability to
 * order (`extensions.code === 'Forbidden'`).
 */
export const BUDGET_EXCEEDED_REASON = "budget_exceeded";

/**
 * ENG-3738 — `extensions.reason` of the submitOrder ValidationErrors a portal
 * user can act on. The portal maps these to its own copy and never renders the
 * server message (which can embed ids); any other refusal gets generic text.
 */
export const SUBMIT_REFUSAL_REASONS = {
  orderingPractitionerRequired: "ordering_practitioner_required",
  materialNotAllowed: "material_not_allowed",
  productNotLive: "product_not_live",
  productNotEntitled: "product_not_entitled",
} as const;
export type SubmitRefusalReason = (typeof SUBMIT_REFUSAL_REASONS)[keyof typeof SUBMIT_REFUSAL_REASONS];

/**
 * ENG-3054 AD-6 (D7) — `extensions.cause` of a `BUDGET_EXCEEDED_REASON`
 * refusal, checked by the server in this precedence:
 *   - `unreadable`: a summary row of the org's 24h usage could not be read,
 *     so the limit cannot be checked;
 *   - `single_change_too_large`: the change alone makes more customers lose
 *     the ability to order than the limit allows;
 *   - `limit`: the change fits the limit on its own but not what is left of
 *     it today.
 * The server sends `retryable: cause === 'limit'` alongside it.
 */
export const BUDGET_EXCEEDED_CAUSES = ["limit", "single_change_too_large", "unreadable"] as const;
export type BudgetExceededCause = (typeof BUDGET_EXCEEDED_CAUSES)[number];

/**
 * ENG-3054 AD-6 — the sentence for a `BUDGET_EXCEEDED_REASON` refusal with
 * `extensions.cause: 'limit'`: the change fits the limit on its own, so it
 * can succeed once earlier changes leave the 24h window.
 *
 * Shared, with the sentences below, so the server message
 * (services/api/src/catalog/entitlements/budget.ts), the denial copy
 * (packages/permissions-ui deny.ts) and the admin toast
 * (apps/main/src/pages/admin/productsTabErrors.ts, and the humanizing link
 * apps/main/src/lib/error-humanizer-link.ts) cannot drift apart. The server
 * sends one of the three cause sentences; only the clients use the fourth
 * (BUDGET_EXCEEDED_UNKNOWN_NOT_RETRYABLE_MESSAGE, D8). No
 * sentence shows a number: the limit, the usage and the change's size stay
 * server-side (the refusal's audit row and log line).
 */
export const BUDGET_EXCEEDED_MESSAGE =
  "This change would go over your organization's daily limit on removing customer access. Try again later or contact support.";

/** ENG-3054 AD-6 (D7) — the sentence for `extensions.cause: 'single_change_too_large'`. */
export const BUDGET_EXCEEDED_SINGLE_CHANGE_MESSAGE =
  "This change alone removes access from more customers than the daily limit allows. Contact support.";

/** ENG-3054 AD-6 (D7) — the sentence for `extensions.cause: 'unreadable'`. */
export const BUDGET_EXCEEDED_UNREADABLE_MESSAGE =
  "Customer access can't be removed right now because the daily limit can't be checked. Contact support.";

/**
 * ENG-3054 AD-6 (D8) — the neutral sentence for a `BUDGET_EXCEEDED_REASON`
 * refusal whose `extensions.cause` is missing or unknown and whose
 * `extensions.retryable` is `false`. The server never sends that shape; it
 * covers a client older than a server that adds a not-retryable cause, so
 * the client claims nothing about why.
 */
export const BUDGET_EXCEEDED_UNKNOWN_NOT_RETRYABLE_MESSAGE =
  "Customer access can't be removed right now. Contact support.";

/** One sentence per cause; `satisfies` makes a new cause without a sentence a compile error. */
const BUDGET_EXCEEDED_SENTENCES = {
  limit: BUDGET_EXCEEDED_MESSAGE,
  single_change_too_large: BUDGET_EXCEEDED_SINGLE_CHANGE_MESSAGE,
  unreadable: BUDGET_EXCEEDED_UNREADABLE_MESSAGE,
} as const satisfies Record<BudgetExceededCause, string>;

/**
 * ENG-3054 AD-6 (D7, D8) — the budget refusal's sentence, chosen by its
 * `extensions.cause`. A missing or unknown cause falls back to
 * `extensions.retryable`: `false` gets the neutral
 * `BUDGET_EXCEEDED_UNKNOWN_NOT_RETRYABLE_MESSAGE` (D8); anything else, an
 * older server that sends neither field included, gets the `limit` sentence.
 *
 * Neither field may trigger an automatic retry. On a budget `Forbidden`,
 * `retryable: true` (`cause: 'limit'`) means only that the same change CAN
 * succeed later, once earlier changes leave the 24h window; that differs
 * from the access-change `Conflict`, whose `retryable: true` means retry
 * now. Branch on `code` first.
 */
export function budgetExceededMessage(cause: unknown, retryable?: unknown): string {
  if (typeof cause === "string" && Object.hasOwn(BUDGET_EXCEEDED_SENTENCES, cause)) {
    return BUDGET_EXCEEDED_SENTENCES[cause as BudgetExceededCause];
  }
  return retryable === false ? BUDGET_EXCEEDED_UNKNOWN_NOT_RETRYABLE_MESSAGE : BUDGET_EXCEEDED_MESSAGE;
}
